//src/app/services/responseTypes/index.ts
import { SpouseOrDependent } from 'app/features/questionnaires/create/PrepareQuestionnaire/slice';
import { TPrepareQuestionnaireState, TTaxQuestionnaire } from 'app/redux/store';
import { IDependent, ISpouse, IUser } from '../user';

export type TQuestionnaireData = {
  taxQuestionnaire?: TTaxQuestionnaire;
  prepareQuestionnaire: TPrepareQuestionnaireState;
};

export interface Questionnaire {
  id: number;
  fbar_only: boolean;
  year: string;
  data: TQuestionnaireData;
  editted: boolean;
}
export interface TeamMembers {
  id: number;
  given_name: string;
  family_name: string;
  email: string;
}
export type ProjectStatusType =
  | 'draft'
  | 'pending_pricing'
  | 'submitted'
  | 'rejected'
  | 'approved'
  | 'assigned'
  | 'pending_pricing'
  | 'pending_payment'
  | 'paid'
  | 'cancelled';
export interface EngagementLetter {
  id: number;
  project_id: number;
  amount: string;
  services: string;
  creator_id: number;
  created_at: string;
  signing_status: string;
  preview_url: string;
  sign_url: string;
}
export interface IProject {
  id: number;
  year?: string;
  title: string;
  service: string;
  status: ProjectStatusType;
  engagement_letter: EngagementLetter;
  price: {
    paid_cents: number;
    amount_cents: number;
    promocode: {
      percentage: number;
      code: string;
    };
  };
  questionnaire: Questionnaire;
  assignees: TeamMembers[];
  rejection_reasons: any[];
  created_at: string;
  updated_at: string;
  tax_return_status?: ProjectStatusType;
  started_at: string;
  businesses: { id: number; name: string; logo_url: string }[];
  clients: IUser[];
  project_beneficiary?: {
    beneficiary_type: SpouseOrDependent.Spouse | SpouseOrDependent.Dependant;
    beneficiary: ISpouse | IDependent;
  };
}

export interface ProjectsResponse {
  projects: IProject[];
}

export interface ProjectsTableResponse {
  id: number;
  year: string;
  status: ProjectStatusType;
  updated_at: string;
  tax_return_status?: string;
  assignees?: TeamMembers[];
  created_at: any;
}

export interface UserResponse {
  user: IUser[];
}
export interface BusinessProjectUpdate {
  id?: number;
  name?: string;
  address?: string;
  bookkeeping_software?: string;
  business_type?: string;
  city?: string;
  state?: string;
  ein?: string;
  fiscal_year_start?: string;
  formation_date?: string;
  formation_state?: string;
  industry?: string;
  operational?: boolean;
  ownership?: string;
  phone_number?: string;
  logo_url?: string;
  subscribed: { [key: string]: boolean }; // Ensure it's a JSON object!
  business_owners_attributes: [
    {
      id?: number;
      firstname?: string;
      lastname?: string;
      ownership_percentage?: number;
      ssn?: string;
      address?: string;
      country?: string;
      city?: string;
      state?: string;
      zipcode?: string;
      _destroy?: boolean;
    },
  ];
  business_owners: [
    {
      id?: number;
      firstname?: string;
      lastname?: string;
      ownership_percentage?: number;
      ssn?: string;
      address?: string;
      country?: string;
      city?: string;
      state?: string;
      zipcode?: string;
    },
  ];
  team_members: TeamMembers[];
}

export interface IClientBusiness {
  id: number;
  name: string;
  address: string;
  bookkeeping_software: string;
  business_type: string;
  city: string;
  state: string;
  ein: string;
  fiscal_year_start: string;
  formation_date: string;
  formation_state: string;
  specialization?: { id: number; name: string };
  operational: boolean;
  ownership: string;
  phone_number: string;
  logo_url: string;
  created_at: string;
  subscribed: { [key: string]: boolean }; // Ensure it's a JSON object!
  business_owners: [
    {
      id: number;
      firstname: string;
      lastname: string;
      ownership_percentage: number;
      ssn: string;
      address: string;
      country: string;
      city: string;
      state: string;
      zipcode: string;
      ein: string;
      fiscal_year_start: string;
      formation_date: string;
      formation_state: string;
      industry: string;
      operational: boolean;
      ownership: string;
      phone_number: string;
      logo_url: string;
      business_owners: [
        {
          id: number;
          firstname: string;
          lastname: string;
          ownership_percentage: number;
          ssn: string;
          address: string;
          country: string;
          city: string;
          state: string;
          zipcode: string;
        },
      ];
      team_members: TeamMembers[];
    },
  ];
  team_members: [
    {
      id: number;
      given_name: string;
      family_name: string;
      email: string;
    },
  ];
  business_id: number;
  business_name: string;
}
export interface BusinessProjectGetAll {
  businesses: IClientBusiness[];
}
export interface BusinessProjectGetId {
  id: number;
  name: string;
  address: string;
  bookkeeping_software: string;
  business_type: string;
  city: string;
  state: string;
  ein: string;
  fiscal_year_start: string;
  formation_date: string;
  formation_state: string;
  specialization?: { id: number; name: string };
  operational: boolean;
  ownership: string;
  phone_number: string;
  logo_url: string;
  zipcode: string;
  subscribed: { [key: string]: boolean }; // Ensure it's a JSON object!
  business_owners: [
    {
      id: number;
      firstname: string;
      lastname: string;
      ownership_percentage: string;
      ssn: string;
      address: string;
      country: string;
      city: string;
      state: string;
      zipcode: string;
      user_id?: number;
      user?: IUser;
    },
  ];
  business_owners_attributes?: BusinessProjectGetId['business_owners'];
  team_members: TeamMembers[];
}
export interface BusinessProjectResponseGetId {
  business: BusinessProjectGetId[];
}
export interface BusinessProjectResponseGetAll {
  business: BusinessProjectGetAll[];
}
export interface BusinessProjectResponseUpdate {
  business: BusinessProjectUpdate[];
}
export interface AssignBusinessTeamClient {
  business_id: number;
  assignee_ids: number[];
}
export interface AssignIndividualTeamClient {
  client_id: number;
  assignee_ids: number[];
}

export enum MatchedAttributeEnum {
  Timezone = 'timezone',
  Language = 'language',
  Specialization = 'specialization',
  Zipcode = 'zipcode',
}
export interface IAccountantRecommendation extends IUser {
  zipcode: string;
  accountant_profile_languages: {
    id: number;
    language_name: {
      id: number;
      name: string;
    };
    language_proficiency_name: {
      id: number;
      name: string;
    };
  }[];
  accountant_profile_specializations: {
    id: number;
    accountant_profile_specialization_name: {
      id: number;
      name: string;
    };
  }[];
  match_percentage: number;
  active_tasks: number;
  matched_attributes: MatchedAttributeEnum[];
  active_projects: number;
}

export enum CardBrandTypeEnum {
  mastercard = 'mastercard',
  unionpay = 'unionpay',
  jcb = 'jcb',
  discover = 'discover',
  cartes_bancaires = 'cartes_bancaires',
  visa = 'visa',
  diners_club = 'diners_club',
  amex = 'amex',
}

export interface Card {
  id: string;
  brand: CardBrandTypeEnum;
  last_4_digits: string;
  exp_year: number;
  exp_month: number;
}

export interface Cards {
  cards: Card[];
  default_payment_method: string;
}
export interface PriceType {
  id: number;
  name: string;
}
export interface Price {
  id: number;
  title: string;
  description: string;
  amount_cents: number;
  price_type: PriceType;
}
export interface Services {
  services: Service[];
}
export interface Service {
  id: number;
  title: string;
  description: string;
  is_bundle: boolean;
  features?: string[];
  prices: Price[];
}

export interface Cart {
  id: number;
  cart_items: CartItem[];
}
export interface CartItem {
  id: number;
  product: {
    id: number;
    type: string;
    title: string;
  };
  price: {
    id?: number;
    amount_cents: number;
    title?: string;
    type: string;
  };
  business?: {
    id: number;
    name: string;
  };
}
export interface IaddCartItemService {
  cart_item: {
    product_id: number;
    price_id: number;
    business_id: number;
  };
}
export interface IaddCartItemProject {
  cart_item: {
    product_id: number;
    type: 'project';
  };
}

export interface IOrder {
  id: string;
  total_price: number;
  state: string;
  user: IUser;
  order_items: IOrderItems[];
  payment_transactions: any[];
  created_at: string;
}

export interface IOrderItems {
  id: number;
  price_meta: {
    id: number;
    amount_cents: number;
    paid_cents: number;
    title: string;
    type: string;
  };
  product_meta: {
    id: number;
    title: string;
    description: string;
    type: 'project' | 'service' | 'bundle';
    features: string[];
  };
  details: {
    business: {
      id: number;
      name: string;
    };
  };
}
export interface ISubscribedServicesItems {
  id: number;
  price_meta: {
    id: number;
    amount_cents: number;
    title: string;
    type: string;
  };
  product_meta: {
    id: number;
    title: string;
    description: string;
    features: string[];
    type: string;
  };
  details: {
    business: {
      id: number;
      name: string;
    };
  };
  state: 'paid' | 'pending_payment' | 'cancelled';
  payment_intent_id?: string;
  user: {
    id: number;
    given_name: string;
    family_name: string;
    email: string;
    profile_id?: number;
    profile_type?: string;
  };
  created_at: string;
}

export interface IcreateOrder {
  order: {
    promo_code?: string;
    cart_items_ids: number[];
  };
}

export interface IcreateOnBehalfOrder {
  order: {
    client_id: number;
    service_id: number;
    price_id: number;
    business_id?: number;
  };
}
